/**
 * MobileEvolucaoComportamental - Página de evolução dos testes DISC e Big Five
 * Mostra gráficos comparativos do histórico de avaliações
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { candidatesAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  ArrowLeft,
  Loader2,
  Brain,
  TrendingUp,
  TrendingDown,
  Minus,
  Calendar,
  BarChart3
} from 'lucide-react';

const DISC_COLORS = {
  D: { bg: 'bg-red-500', text: 'text-red-500', nome: 'Dominância', light: 'bg-red-100' },
  I: { bg: 'bg-yellow-500', text: 'text-yellow-500', nome: 'Influência', light: 'bg-yellow-100' },
  S: { bg: 'bg-green-500', text: 'text-green-500', nome: 'Estabilidade', light: 'bg-green-100' },
  C: { bg: 'bg-blue-500', text: 'text-blue-500', nome: 'Conformidade', light: 'bg-blue-100' }
};

const BIGFIVE_COLORS = {
  O: { bg: 'bg-purple-500', text: 'text-purple-500', nome: 'Abertura', light: 'bg-purple-100' },
  C: { bg: 'bg-blue-500', text: 'text-blue-500', nome: 'Conscienciosidade', light: 'bg-blue-100' },
  E: { bg: 'bg-yellow-500', text: 'text-yellow-500', nome: 'Extroversão', light: 'bg-yellow-100' },
  A: { bg: 'bg-green-500', text: 'text-green-500', nome: 'Amabilidade', light: 'bg-green-100' },
  N: { bg: 'bg-red-500', text: 'text-red-500', nome: 'Neuroticismo', light: 'bg-red-100' }
};

// Componente de barra de progresso
const ScoreBar = ({ label, value, maxValue = 100, color, previousValue }) => {
  const percentage = Math.min((value / maxValue) * 100, 100);
  const prevPercentage = previousValue !== null ? Math.min((previousValue / maxValue) * 100, 100) : null;
  const diff = previousValue !== null ? value - previousValue : null;
  
  return (
    <div className="mb-3">
      <div className="flex justify-between items-center mb-1">
        <span className="text-sm text-slate-300">{label}</span>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-white">{value}%</span>
          {diff !== null && diff !== 0 && (
            <span className={`text-xs flex items-center ${diff > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {diff > 0 ? <TrendingUp className="w-3 h-3 mr-0.5" /> : <TrendingDown className="w-3 h-3 mr-0.5" />}
              {diff > 0 ? '+' : ''}{diff}%
            </span>
          )}
        </div>
      </div>
      <div className="h-3 bg-slate-700 rounded-full overflow-hidden relative">
        {/* Barra anterior (fantasma) */}
        {prevPercentage !== null && (
          <div 
            className="absolute h-full bg-slate-600 rounded-full transition-all duration-500"
            style={{ width: `${prevPercentage}%` }}
          />
        )}
        {/* Barra atual */}
        <div 
          className={`h-full ${color} rounded-full transition-all duration-500 relative z-10`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

// Componente de timeline
const TimelineItem = ({ item, index, total, type }) => {
  const isLast = index === total - 1;
  const colors = type === 'disc' ? DISC_COLORS : BIGFIVE_COLORS;
  const perfil = type === 'disc' ? item.perfil : item.result?.dominant;
  const perfilConfig = colors[perfil?.[0]];
  
  return (
    <div className="flex gap-4">
      {/* Linha vertical */}
      <div className="flex flex-col items-center">
        <div className={`w-4 h-4 rounded-full ${perfilConfig?.bg || 'bg-slate-500'}`} />
        {!isLast && <div className="w-0.5 h-full bg-slate-700 my-1" />}
      </div>
      
      {/* Conteúdo */}
      <div className={`flex-1 pb-6 ${isLast ? '' : ''}`}>
        <div className="flex items-center gap-2 mb-1">
          <Badge className={`${perfilConfig?.light || 'bg-slate-100'} ${perfilConfig?.text || 'text-slate-700'}`}>
            {perfil}
          </Badge>
          <span className="text-xs text-slate-500">
            {new Date(item.data).toLocaleDateString('pt-BR')}
          </span>
        </div>
        
        {/* Mini gráfico de scores */}
        {item.scores && (
          <div className="grid grid-cols-4 gap-1 mt-2">
            {Object.entries(item.scores).map(([key, value]) => (
              <div key={key} className="text-center">
                <div className="text-xs text-slate-500">{key}</div>
                <div className={`text-sm font-medium ${colors[key]?.text || 'text-white'}`}>
                  {Math.round(value)}%
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default function MobileEvolucaoComportamental() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [candidate, setCandidate] = useState(null);
  const [activeTab, setActiveTab] = useState('disc');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const response = await candidatesAPI.getMe();
      setCandidate(response.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const discHistorico = candidate?.disc_historico || [];
  const bigfiveHistorico = candidate?.bigfive_historico || [];
  
  // Pegar último e penúltimo para comparação
  const lastDisc = discHistorico.length > 0 ? discHistorico[discHistorico.length - 1] : null;
  const prevDisc = discHistorico.length > 1 ? discHistorico[discHistorico.length - 2] : null;
  
  const lastBigFive = bigfiveHistorico.length > 0 ? bigfiveHistorico[bigfiveHistorico.length - 1] : null;
  const prevBigFive = bigfiveHistorico.length > 1 ? bigfiveHistorico[bigfiveHistorico.length - 2] : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 pb-6" data-testid="mobile-evolucao">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-slate-900/80 backdrop-blur-sm p-4 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-slate-400">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-white">Evolução Comportamental</h1>
            <p className="text-xs text-slate-400">Histórico das suas avaliações</p>
          </div>
        </div>
      </div>

      <div className="px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full bg-slate-800 mb-6">
            <TabsTrigger value="disc" className="flex-1 data-[state=active]:bg-violet-600">
              <Brain className="w-4 h-4 mr-2" />
              DISC ({discHistorico.length})
            </TabsTrigger>
            <TabsTrigger value="bigfive" className="flex-1 data-[state=active]:bg-blue-600">
              <BarChart3 className="w-4 h-4 mr-2" />
              Big Five ({bigfiveHistorico.length})
            </TabsTrigger>
          </TabsList>

          {/* Tab DISC */}
          <TabsContent value="disc">
            {discHistorico.length === 0 ? (
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-6 text-center">
                  <Brain className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-white font-medium mb-2">Nenhuma avaliação DISC</h3>
                  <p className="text-slate-400 text-sm mb-4">
                    Faça seu primeiro teste DISC para começar a acompanhar sua evolução.
                  </p>
                  <Button onClick={() => navigate('/m/disc')} className="bg-violet-600 hover:bg-violet-700">
                    Fazer teste DISC
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* Comparativo Atual */}
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-white font-semibold">Perfil Atual</h3>
                      {discHistorico.length > 1 && (
                        <Badge className="bg-emerald-900/50 text-emerald-400 border border-emerald-500/30">
                          <TrendingUp className="w-3 h-3 mr-1" />
                          {discHistorico.length} avaliações
                        </Badge>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4 mb-6">
                      <div className={`w-16 h-16 rounded-xl ${DISC_COLORS[lastDisc?.perfil?.[0]]?.bg || 'bg-slate-600'} flex items-center justify-center`}>
                        <span className="text-2xl font-bold text-white">{lastDisc?.perfil}</span>
                      </div>
                      <div>
                        <p className="text-white font-medium">
                          {DISC_COLORS[lastDisc?.perfil?.[0]]?.nome || 'Perfil'}
                        </p>
                        <p className="text-slate-400 text-sm">
                          {new Date(lastDisc?.data).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                    </div>

                    {/* Barras de scores com comparação */}
                    {lastDisc?.scores && (
                      <div>
                        {Object.entries(lastDisc.scores).map(([key, value]) => (
                          <ScoreBar
                            key={key}
                            label={`${key} - ${DISC_COLORS[key]?.nome}`}
                            value={Math.round(value)}
                            color={DISC_COLORS[key]?.bg}
                            previousValue={prevDisc?.scores?.[key] ? Math.round(prevDisc.scores[key]) : null}
                          />
                        ))}
                      </div>
                    )}

                    {prevDisc && (
                      <p className="text-center text-slate-500 text-xs mt-4">
                        Comparando com avaliação de {new Date(prevDisc.data).toLocaleDateString('pt-BR')}
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Timeline de histórico */}
                {discHistorico.length > 1 && (
                  <Card className="bg-slate-800 border-slate-700">
                    <CardContent className="p-6">
                      <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Histórico
                      </h3>
                      <div>
                        {[...discHistorico].reverse().map((item, index) => (
                          <TimelineItem
                            key={index}
                            item={item}
                            index={index}
                            total={discHistorico.length}
                            type="disc"
                          />
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>

          {/* Tab Big Five */}
          <TabsContent value="bigfive">
            {bigfiveHistorico.length === 0 ? (
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-6 text-center">
                  <BarChart3 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-white font-medium mb-2">Nenhuma avaliação Big Five</h3>
                  <p className="text-slate-400 text-sm mb-4">
                    Faça seu primeiro teste Big Five para começar a acompanhar sua evolução.
                  </p>
                  <Button onClick={() => navigate('/m/bigfive')} className="bg-blue-600 hover:bg-blue-700">
                    Fazer teste Big Five
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* Comparativo Atual */}
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-white font-semibold">Perfil Atual</h3>
                      {bigfiveHistorico.length > 1 && (
                        <Badge className="bg-blue-900/50 text-blue-400 border border-blue-500/30">
                          <TrendingUp className="w-3 h-3 mr-1" />
                          {bigfiveHistorico.length} avaliações
                        </Badge>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4 mb-6">
                      <div className={`w-16 h-16 rounded-xl ${BIGFIVE_COLORS[lastBigFive?.result?.dominant]?.bg || 'bg-slate-600'} flex items-center justify-center`}>
                        <span className="text-xl font-bold text-white">{lastBigFive?.result?.dominant}</span>
                      </div>
                      <div>
                        <p className="text-white font-medium">
                          {BIGFIVE_COLORS[lastBigFive?.result?.dominant]?.nome || 'Perfil'}
                        </p>
                        <p className="text-slate-400 text-sm">
                          {new Date(lastBigFive?.data).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                    </div>

                    {/* Barras de scores com comparação */}
                    {lastBigFive?.scores && (
                      <div>
                        {Object.entries(lastBigFive.scores).map(([key, value]) => (
                          <ScoreBar
                            key={key}
                            label={`${key} - ${BIGFIVE_COLORS[key]?.nome}`}
                            value={Math.round(value)}
                            color={BIGFIVE_COLORS[key]?.bg}
                            previousValue={prevBigFive?.scores?.[key] ? Math.round(prevBigFive.scores[key]) : null}
                          />
                        ))}
                      </div>
                    )}

                    {prevBigFive && (
                      <p className="text-center text-slate-500 text-xs mt-4">
                        Comparando com avaliação de {new Date(prevBigFive.data).toLocaleDateString('pt-BR')}
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Timeline de histórico */}
                {bigfiveHistorico.length > 1 && (
                  <Card className="bg-slate-800 border-slate-700">
                    <CardContent className="p-6">
                      <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Histórico
                      </h3>
                      <div>
                        {[...bigfiveHistorico].reverse().map((item, index) => (
                          <TimelineItem
                            key={index}
                            item={item}
                            index={index}
                            total={bigfiveHistorico.length}
                            type="bigfive"
                          />
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
